import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '../modal';
import { Button } from '../button';
import { SCREENS_PX } from '../responsive';
import { formatAddress } from '../../helpers/formatAddress';
import { PickupCatalogLocation } from '../../utils/pickupLocationGroups';
import { GroupRow, LocationRow } from './LocationRows';
import { PickerNavigation, usePickerNavigation } from './usePickerNavigation';
import {
  CascadeList,
  CascadePanel,
  CascadeTop,
  Column,
  ColumnList,
  ColumnTitle,
  Columns,
  Crumbs,
  FromLine,
  Hint,
  Message,
  ModalLayout,
  ModalTitle,
  MoreHint,
  Results,
  SearchBox,
  Skeleton,
} from './styles';

export type PickupPickerKind = 'pickup' | 'return';

export interface PickupLocationPickerProps<T extends PickupCatalogLocation> {
  kind: PickupPickerKind;
  /** Owned by the field: the picker shows while true. */
  open: boolean;
  onClose: () => void;
  locations: readonly T[];
  value: T | null | undefined;
  onSelect: (location: T) => void;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** Return picker: the chosen pickup. Opens on its state and city, and is left out (FR-019). */
  anchor?: (PickupCatalogLocation & { address?: string | null }) | null;
  /** Always the modal (the results page's horizontal search bar). */
  forceModal?: boolean;
}

/** Spec 022 FR-013 / FR-014: the modal from Tailwind `lg` up, the inline cascade below. */
export const pickerLayoutFor = (width: number, forceModal?: boolean): 'modal' | 'cascade' =>
  forceModal || width >= SCREENS_PX.lg ? 'modal' : 'cascade';

const TITLES: Record<PickupPickerKind, string> = {
  pickup: 'Choose a pickup location',
  return: 'Choose a return location',
};

/**
 * Spec 022: the guest picks a catalog location by state, then city, then
 * address, or by searching. The layout is chosen when it opens and kept until
 * it closes, so resizing never swaps it mid-choice.
 */
export function PickupLocationPicker<T extends PickupCatalogLocation>(
  props: PickupLocationPickerProps<T>,
) {
  const layoutRef = useRef<'modal' | 'cascade' | null>(null);
  if (!props.open) {
    layoutRef.current = null;
    return null;
  }
  if (!layoutRef.current) {
    layoutRef.current = pickerLayoutFor(window.innerWidth, props.forceModal);
  }
  return <OpenPicker {...props} layout={layoutRef.current} />;
}

function OpenPicker<T extends PickupCatalogLocation>({
  kind,
  onClose,
  locations,
  value,
  onSelect,
  loading,
  error,
  onRetry,
  anchor,
  layout,
}: PickupLocationPickerProps<T> & { layout: 'modal' | 'cascade' }) {
  const choices = useMemo(
    () => (anchor ? locations.filter(location => location.id !== anchor.id) : locations),
    [locations, anchor],
  );
  const nav = usePickerNavigation(choices, value?.id ?? null, anchor);
  const rootRef = useRef<HTMLDivElement>(null);

  const choose = useCallback(
    (location: T) => {
      onSelect(location);
      onClose();
    },
    [onSelect, onClose],
  );

  // Esc closes (FR-024); focus goes back to where it was when the picker opened.
  // The field passes a new callback on every render, so read it through a ref.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    const target = event.target as HTMLElement;
    if (!root) return;
    if (event.key === 'Enter' && target.tagName === 'INPUT' && nav.searching) {
      event.preventDefault();
      if (nav.results[0]) choose(nav.results[0]);
      return;
    }
    const list = target.closest('[role="listbox"]');
    if (list && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const options = Array.from(list.querySelectorAll<HTMLElement>('[role="option"]'));
      const at = options.indexOf(target);
      const next =
        event.key === 'ArrowDown' ? Math.min(at + 1, options.length - 1) : Math.max(at - 1, 0);
      options[next]?.focus();
      return;
    }
    if (list && layout === 'modal' && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      const lists = Array.from(root.querySelectorAll<HTMLElement>('[role="listbox"]'));
      const other =
        lists[lists.indexOf(list as HTMLElement) + (event.key === 'ArrowRight' ? 1 : -1)];
      const option =
        other?.querySelector<HTMLElement>('[aria-selected="true"]') ??
        other?.querySelector<HTMLElement>('[role="option"]');
      if (option) {
        event.preventDefault();
        option.focus();
      }
      return;
    }
    if (event.key === 'Tab' && layout === 'modal') {
      // Keep focus inside the dialog.
      const focusable = Array.from(
        root.closest('[role="dialog"]')?.querySelectorAll<HTMLElement>('button, input') ?? [],
      ).filter(element => !element.hasAttribute('disabled'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };

  const fromLine =
    kind === 'return' && anchor ? (
      <FromLine>Return options for pickup at {formatAddress(anchor) || anchor.name}</FromLine>
    ) : null;

  const search = (
    <SearchBox>
      <FontAwesomeIcon icon={faSearch} aria-hidden />
      <input
        type="search"
        id={`pickup-picker-search-${kind}`}
        aria-label="Search locations"
        placeholder="Search by city, address or airport"
        autoComplete="off"
        value={nav.query}
        onChange={event => nav.setQuery(event.target.value)}
        autoFocus={layout === 'modal'}
        data-testid="pickup-picker-search"
      />
    </SearchBox>
  );

  const status = statusMessage(kind, { loading, error, empty: choices.length === 0, onRetry });

  if (layout === 'modal') {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title={TITLES[kind]}
        modalWidth="min(880px, calc(100vw - 48px))"
        modalHeight="min(620px, 85vh)"
      >
        <ModalLayout ref={rootRef} onKeyDown={onKeyDown} data-testid="pickup-picker-modal">
          <ModalTitle>{TITLES[kind]}</ModalTitle>
          {fromLine}
          {search}
          {status ? (
            <Results>{status}</Results>
          ) : nav.searching ? (
            <Results>
              <SearchResults nav={nav} value={value} onChoose={choose} />
            </Results>
          ) : (
            <ModalColumns nav={nav} value={value} onChoose={choose} />
          )}
        </ModalLayout>
      </Modal>
    );
  }

  return (
    <CascadePanel
      ref={rootRef}
      onKeyDown={onKeyDown}
      data-testid="pickup-picker-cascade"
      aria-label={TITLES[kind]}
      role="group"
    >
      <CascadeTop>
        {fromLine}
        {search}
      </CascadeTop>
      {status ? <Message>{status}</Message> : <Cascade nav={nav} value={value} onChoose={choose} />}
    </CascadePanel>
  );
}

function statusMessage(
  kind: PickupPickerKind,
  {
    loading,
    error,
    empty,
    onRetry,
  }: { loading?: boolean; error?: boolean; empty: boolean; onRetry?: () => void },
): React.ReactNode {
  if (loading) {
    return (
      <div aria-busy="true" aria-label="Loading locations">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <Message role="alert">
        <strong>Could not load locations.</strong>
        {onRetry && (
          <Button type="button" theme="secondary" size="sm" text="Retry" onClick={onRetry} />
        )}
      </Message>
    );
  }
  if (empty) {
    return kind === 'pickup' ? (
      <Message>
        <strong>No pickup locations are available on this site yet.</strong>
        <span>Try Enter Address instead.</span>
      </Message>
    ) : (
      <Message>
        <strong>No return locations are configured for this pickup location.</strong>
      </Message>
    );
  }
  return null;
}

interface ViewProps<T extends PickupCatalogLocation> {
  nav: PickerNavigation<T>;
  value: T | null | undefined;
  onChoose: (location: T) => void;
  tall?: boolean;
}

function SearchResults<T extends PickupCatalogLocation>({
  nav,
  value,
  onChoose,
  tall,
}: ViewProps<T>) {
  if (nav.results.length === 0) {
    return (
      <Message>
        <strong>No locations match “{nav.query.trim()}”.</strong>
        <span>Try another spelling, or use Enter Address.</span>
      </Message>
    );
  }
  return (
    <div role="listbox" aria-label="Matching locations">
      {nav.results.map(location => (
        <LocationRow
          key={location.id}
          location={location}
          selected={location.id === value?.id}
          query={nav.query}
          tall={tall}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
}

function useScrollSelectedIntoView(deps: unknown[]) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const selected = ref.current?.querySelector<HTMLElement>(
      '[data-testid^="pickup-picker-location-"][aria-selected="true"]',
    );
    selected?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

function ModalColumns<T extends PickupCatalogLocation>({ nav, value, onChoose }: ViewProps<T>) {
  const locationsRef = useScrollSelectedIntoView([nav.cityKey]);
  return (
    <Columns>
      <Column>
        <ColumnTitle id="pickup-picker-col-state">State</ColumnTitle>
        <ColumnList role="listbox" aria-labelledby="pickup-picker-col-state">
          {nav.groups.map(state => (
            <GroupRow
              key={state.key}
              name={state.name}
              count={state.locationCount}
              selected={state.key === nav.stateKey}
              onChoose={() => nav.chooseState(state.key)}
              testId={`pickup-picker-state-${state.key}`}
            />
          ))}
        </ColumnList>
      </Column>
      <Column>
        <ColumnTitle id="pickup-picker-col-city">City</ColumnTitle>
        {nav.state ? (
          <ColumnList role="listbox" aria-labelledby="pickup-picker-col-city">
            {nav.state.cities.map(city => (
              <GroupRow
                key={city.key}
                name={city.name}
                count={city.locations.length}
                selected={city.key === nav.cityKey}
                onChoose={() => nav.chooseCity(city.key)}
                testId={`pickup-picker-city-${city.key}`}
              />
            ))}
          </ColumnList>
        ) : (
          <Hint>Choose a state</Hint>
        )}
      </Column>
      <Column>
        <ColumnTitle id="pickup-picker-col-location">Location</ColumnTitle>
        {nav.city ? (
          <ColumnList
            ref={locationsRef}
            role="listbox"
            aria-labelledby="pickup-picker-col-location"
          >
            {nav.city.locations.map(location => (
              <LocationRow
                key={location.id}
                location={location}
                selected={location.id === value?.id}
                onChoose={onChoose}
              />
            ))}
          </ColumnList>
        ) : (
          <Hint>Choose a city</Hint>
        )}
      </Column>
    </Columns>
  );
}

const LEVEL_NOUN = { 0: 'states', 1: 'cities', 2: 'locations' } as const;

function Cascade<T extends PickupCatalogLocation>({ nav, value, onChoose }: ViewProps<T>) {
  const level: 0 | 1 | 2 = nav.city ? 2 : nav.state ? 1 : 0;
  const listRef = useScrollSelectedIntoView([level, nav.searching]);
  const [more, setMore] = useState(false);

  const measure = useCallback(() => {
    const list = listRef.current;
    setMore(!!list && list.scrollHeight - list.scrollTop - list.clientHeight > 4);
  }, [listRef]);
  useLayoutEffect(measure, [measure, level, nav.stateKey, nav.cityKey, nav.query, nav.groups]);

  const rowCount = nav.searching
    ? nav.results.length
    : level === 0
      ? nav.groups.length
      : level === 1
        ? (nav.state?.cities.length ?? 0)
        : (nav.city?.locations.length ?? 0);

  return (
    <>
      {!nav.searching && (
        <Crumbs aria-label="Location levels">
          <button
            type="button"
            className="back"
            aria-label="Back"
            onClick={nav.back}
            disabled={level === 0}
            data-testid="pickup-picker-back"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {level === 0 ? (
            <span className="current">All states</span>
          ) : (
            <button type="button" onClick={nav.goToStates}>
              All states
            </button>
          )}
          {nav.state && (
            <>
              <span className="sep" aria-hidden>
                ›
              </span>
              {level === 1 ? (
                <span className="current">{nav.state.name}</span>
              ) : (
                <button type="button" onClick={nav.goToState}>
                  {nav.state.name}
                </button>
              )}
            </>
          )}
          {nav.city && (
            <>
              <span className="sep" aria-hidden>
                ›
              </span>
              <span className="current">{nav.city.name}</span>
            </>
          )}
        </Crumbs>
      )}
      <CascadeList
        key={nav.searching ? 'search' : `${level}:${nav.stateKey}:${nav.cityKey}`}
        ref={listRef}
        $more={more}
        onScroll={measure}
        data-testid="pickup-picker-list"
      >
        {nav.searching ? (
          <SearchResults nav={nav} value={value} onChoose={onChoose} tall />
        ) : level === 0 ? (
          <div role="listbox" aria-label="State">
            {nav.groups.map(state => (
              <GroupRow
                key={state.key}
                name={state.name}
                count={state.locationCount}
                selected={false}
                tall
                onChoose={() => nav.chooseState(state.key)}
                testId={`pickup-picker-state-${state.key}`}
              />
            ))}
          </div>
        ) : level === 1 && nav.state ? (
          <div role="listbox" aria-label="City">
            {nav.state.cities.map(city => (
              <GroupRow
                key={city.key}
                name={city.name}
                count={city.locations.length}
                selected={false}
                tall
                onChoose={() => nav.chooseCity(city.key)}
                testId={`pickup-picker-city-${city.key}`}
              />
            ))}
          </div>
        ) : (
          <div role="listbox" aria-label="Location">
            {nav.city?.locations.map(location => (
              <LocationRow
                key={location.id}
                location={location}
                selected={location.id === value?.id}
                tall
                onChoose={onChoose}
              />
            ))}
          </div>
        )}
      </CascadeList>
      {more && (
        <MoreHint aria-hidden data-testid="pickup-picker-more">
          Scroll for more · {rowCount} {nav.searching ? 'locations' : LEVEL_NOUN[level]}
        </MoreHint>
      )}
    </>
  );
}

export default PickupLocationPicker;
